import type { ListViewConfig } from './list-view';
import { beijingDay, type FilterRow } from './query';
import { dateSort } from './tracking-config';

export type PersonnelPage =
  'pending-offer' | 'sent-offer' | 'pending-onboarding' | 'onboarded' | 'signature' | 'cancelled' | 'archive';
type Personnel = {
  id: string;
  candidateName?: string;
  departmentName?: string;
  departmentId?: string;
  employeeType?: string;
  position?: string;
  expectedOnboardDate?: string;
  arrivedAt?: string;
  emailSentAt?: string;
  acceptedAt?: string;
  createdAt?: string;
  workflowStageUpdatedAt?: string;
  workflowStage?: string;
  workflowStageRemark?: string;
  hrOwnerName?: string;
  status?: string;
  generatedDocuments?: { generatedAt: string }[];
};
const dateLabels: Record<string, string> = {
  expected: '预计入职日期',
  arrived: '实际入职日期',
  sent: '发送时间',
  accepted: '接受时间',
  created: '创建时间',
  stage: '取消/归档时间',
  generated: '最近生成时间'
};
export function personnelConfig(page: PersonnelPage): ListViewConfig {
  const dateKeys = {
    'pending-offer': ['expected', 'created'],
    'sent-offer': ['sent', 'accepted', 'expected'],
    'pending-onboarding': ['expected'],
    onboarded: ['arrived', 'expected'],
    signature: ['expected', 'arrived', 'generated'],
    cancelled: ['stage', 'expected'],
    archive: ['stage', 'expected', 'created']
  }[page];
  const dates = dateKeys.map((key) => ({ key, label: dateLabels[key] }));
  const facets = [
    { key: 'status', label: '状态' },
    { key: 'department', label: '部门' },
    { key: 'employeeType', label: '员工类型' }
  ];
  if (page.includes('offer')) facets.push({ key: 'approval', label: '审批进度' });
  if (page === 'pending-onboarding' || page === 'onboarded')
    facets.push(
      { key: 'account', label: '钉钉账号' },
      { key: 'email', label: '邮箱状态' },
      { key: 'hrm', label: '人事同步' }
    );
  if (page === 'signature') facets.push({ key: 'documents', label: '文件状态' });
  if (page === 'archive') facets.push({ key: 'stage', label: '当前环节' });
  return {
    key: page,
    unit: '人',
    facets,
    dates,
    sorts: dates,
    groups: [
      { key: 'department', label: '按部门' },
      { key: 'onboardMonth', label: '按入职月份' },
      { key: 'status', label: '按状态' }
    ],
    defaults: {
      keyword: '',
      selected: {},
      date: { field: dateKeys[0], mode: 'ALL' },
      sort: { field: dateKeys[0], direction: ['pending-offer', 'pending-onboarding'].includes(page) ? 'asc' : 'desc' },
      groupBy: ''
    }
  };
}
export function projectPersonnel(
  row: Personnel,
  _index: number,
  extra: Record<string, string | undefined> = {}
): FilterRow {
  const generated = row.generatedDocuments
    ?.map((item) => item.generatedAt)
    .filter((value) => beijingDay(value))
    .sort((a, b) => Date.parse(a) - Date.parse(b))
    .at(-1);
  const dates = {
    expected: row.expectedOnboardDate,
    arrived: row.arrivedAt,
    sent: row.emailSentAt,
    accepted: row.acceptedAt,
    created: row.createdAt,
    stage: ['ARCHIVED', 'CANCELLED'].includes(row.workflowStage ?? '') ? row.workflowStageUpdatedAt : undefined,
    generated
  };
  return {
    id: row.id,
    search: [row.candidateName, row.departmentName, row.position, row.workflowStageRemark].filter(Boolean).join(' '),
    facets: {
      status: row.status,
      department: row.departmentId ? `部门ID:${row.departmentId}` : row.departmentName,
      employeeType: row.employeeType,
      owner: row.hrOwnerName,
      onboardMonth: beijingDay(row.arrivedAt || row.expectedOnboardDate)?.slice(0, 7) ?? '未记录',
      ...extra
    },
    facetLabels: { department: row.departmentName },
    dates,
    sort: dateSort(dates)
  };
}
